import { Calendar } from '@/components/ui/calendar';
import {
  FilterPopoverTrigger,
  type FilterPopoverTriggerVisualProps,
} from '@/components/ui/filter-popover-trigger';
import { cn } from '@/utils/classUtils';
import { startOfDay } from 'date-fns';
import { Check, ChevronLeft, ChevronRight } from 'lucide-react';
import { type FC, useCallback, useMemo, useState } from 'react';
import type { DateRange } from 'react-day-picker';
import {
  boundsToIso,
  formatCustomSummary,
  getPresetRows,
  presetSummary,
  type LastUpdatedPreset,
  type PresetRowId,
} from '../components/datePresetBounds';

// Re-export for consumers that still import from here
export { LAST_UPDATED_PRESET_VALUES } from '../components/datePresetBounds';
export type { LastUpdatedPreset } from '../components/datePresetBounds';

type LastUpdatedFilterProps = {
  effectivePreset: LastUpdatedPreset;
  lastUpdatedFrom: string;
  lastUpdatedTo: string;
  setLastUpdatedPreset: (v: LastUpdatedPreset) => void;
  setLastUpdatedFrom: (v: string) => void;
  setLastUpdatedTo: (v: string) => void;
  clearLastUpdated: () => void;
  onApplyCustomRange?: (from: string, to: string) => void;
  filterPopoverProps?: FilterPopoverTriggerVisualProps;
};

export const LastUpdatedFilter: FC<LastUpdatedFilterProps> = ({
  effectivePreset,
  lastUpdatedFrom,
  lastUpdatedTo,
  setLastUpdatedPreset,
  setLastUpdatedFrom,
  setLastUpdatedTo,
  clearLastUpdated,
  onApplyCustomRange,
  filterPopoverProps,
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [panel, setPanel] = useState<'list' | 'custom'>('list');
  const [customRange, setCustomRange] = useState<DateRange | undefined>(
    undefined
  );

  const isActive =
    effectivePreset !== 'none' &&
    (effectivePreset !== 'custom' || Boolean(lastUpdatedFrom && lastUpdatedTo));

  const activeSummary = useMemo(() => {
    if (!isActive) return undefined;
    if (effectivePreset === 'custom' && lastUpdatedFrom && lastUpdatedTo) {
      return formatCustomSummary(lastUpdatedFrom, lastUpdatedTo);
    }
    if (effectivePreset !== 'custom') {
      return presetSummary(effectivePreset, new Date());
    }
    return 'Custom range';
  }, [isActive, effectivePreset, lastUpdatedFrom, lastUpdatedTo]);

  const presetRows = getPresetRows(new Date());

  const handleOpenChange = useCallback((open: boolean) => {
    setMenuOpen(open);
    if (!open) {
      setPanel('list');
    }
  }, []);

  const applyPreset = useCallback(
    (id: PresetRowId) => {
      setLastUpdatedPreset(id);
      setMenuOpen(false);
      setPanel('list');
    },
    [setLastUpdatedPreset]
  );

  const openCustomPanel = useCallback(() => {
    if (effectivePreset === 'custom' && lastUpdatedFrom && lastUpdatedTo) {
      const fromD = new Date(lastUpdatedFrom);
      const toD = new Date(lastUpdatedTo);
      if (!Number.isNaN(fromD.getTime()) && !Number.isNaN(toD.getTime())) {
        setCustomRange({ from: startOfDay(fromD), to: startOfDay(toD) });
      } else {
        setCustomRange(undefined);
      }
    } else {
      setCustomRange(undefined);
    }
    setPanel('custom');
  }, [effectivePreset, lastUpdatedFrom, lastUpdatedTo]);

  const optionRowClass =
    'flex w-full items-center justify-between gap-2 px-3 py-1.5 text-sm hover:bg-accent focus-visible:outline-none focus-visible:bg-accent';

  return (
    <FilterPopoverTrigger
      label="Last updated"
      isActive={isActive}
      operator={isActive ? 'between' : undefined}
      activeContent={activeSummary}
      onClear={
        isActive
          ? () => {
              clearLastUpdated();
              setMenuOpen(false);
              setPanel('list');
            }
          : undefined
      }
      open={menuOpen}
      onOpenChange={handleOpenChange}
      {...filterPopoverProps}
    >
      {panel === 'list' ? (
        <div className="py-1 min-w-[220px]">
          {presetRows.map(p => {
            const selected = effectivePreset === p.id;
            return (
              <button
                key={p.id}
                type="button"
                className={optionRowClass}
                onClick={() => applyPreset(p.id)}
              >
                <span className="truncate text-left">{p.label}</span>
                {selected ? (
                  <Check className="h-4 w-4 shrink-0 text-primary" />
                ) : (
                  <span className="h-4 w-4 shrink-0" />
                )}
              </button>
            );
          })}
          <button
            type="button"
            className={cn(optionRowClass, 'text-left')}
            onClick={openCustomPanel}
          >
            <span className="truncate">Custom date range</span>
            <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
          </button>
        </div>
      ) : (
        <div className="flex flex-col">
          <button
            type="button"
            onClick={() => setPanel('list')}
            className="flex items-center gap-1.5 px-3 py-2 text-sm text-muted-foreground hover:text-foreground transition-colors border-b"
          >
            <ChevronLeft className="size-4" />
            Back
          </button>
          <Calendar
            mode="range"
            selected={customRange}
            onSelect={range => {
              const prevFrom = customRange?.from;
              setCustomRange(range);
              // Apply when both dates are selected (second click)
              if (
                range?.from &&
                range?.to &&
                prevFrom &&
                range.from.getTime() === prevFrom.getTime()
              ) {
                let from = range.from;
                let to = range.to;
                if (from > to) {
                  const t = from;
                  from = to;
                  to = t;
                }
                const iso = boundsToIso(from, to);
                if (onApplyCustomRange) {
                  onApplyCustomRange(iso.from, iso.to);
                } else {
                  setLastUpdatedPreset('custom');
                  setLastUpdatedFrom(iso.from);
                  setLastUpdatedTo(iso.to);
                }
                // Don't close — user closes by clicking outside
              }
            }}
            numberOfMonths={1}
            defaultMonth={customRange?.from ?? new Date()}
            initialFocus
          />
        </div>
      )}
    </FilterPopoverTrigger>
  );
};
